// src/components/DataExtractor.jsx
import React, { useEffect, useState } from 'react';
import { ArrowLeft, Download } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import ProcessingStatus from './ProcessingStatus';
import Button from './Button';
import { processPdfsWithProgress } from '../api';
import { useTheme } from '../contexts/ThemeContext';

export default function DataExtractor() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const files = state?.files || [];
  const { darkMode } = useTheme();

  const steps = ['Uploading files…', 'Running Docling & Qwen extraction…', 'Finalizing…'];
  const [currentStep, setCurrentStep] = useState(0);
  const [processing, setProcessing] = useState(true);
  const [results, setResults] = useState(null);
  const [progress, setProgress] = useState(0);

  // Start processing when we land here
  useEffect(() => {
    if (!files.length) {
      navigate('/');
      return;
    }

    let creepTimer = null;

    (async () => {
      try {
        // Step 1: real upload progress (0-100% of the transfer) mapped to 0-30%
        setCurrentStep(0);
        setProgress(2);
        const data = await processPdfsWithProgress(files, (pct) => {
          setProgress(Math.max(2, Math.round(pct * 30)));
          if (pct >= 1) {
            // transfer done - the server is now extracting; show honest creep
            setCurrentStep(1);
            setProgress((p) => Math.max(p, 32));
            if (!creepTimer) {
              creepTimer = setInterval(() => {
                setProgress((p) => (p < 80 ? p + 1 : p));
              }, 700);
            }
          }
        });

        if (creepTimer) clearInterval(creepTimer);

        const processedResults = data.results ?? {};

        // Step 3: Finalizing
        setCurrentStep(2);
        setProgress(85);

        // Save results to localStorage
        const resultEntry = {
          timestamp: new Date().toISOString(),
          filename: files.length === 1 ? files[0].name : `${files.length} files`,
          data: processedResults,
          fileCount: files.length
        };

        const existingResults = JSON.parse(localStorage.getItem('pdfResults') || '[]');
        const updatedResults = [resultEntry, ...existingResults];
        const limitedResults = updatedResults.slice(0, 50);
        localStorage.setItem('pdfResults', JSON.stringify(limitedResults));

        setResults(processedResults);
        setProgress(100);
        await new Promise(r => setTimeout(r, 500)); // Brief pause to show 100%

      } catch (e) {
        console.error(e);
        alert(e.message || 'Processing failed');
        navigate('/');
      } finally {
        if (creepTimer) clearInterval(creepTimer);
        setProcessing(false);
      }
    })();

    return () => {
      if (creepTimer) clearInterval(creepTimer);
    };
  }, [files, navigate]);

  // After results arrive, go to /results
  useEffect(() => {
    if (!processing && results) {
      navigate('/results', { state: { results } });
    }
  }, [processing, results, navigate]);

  const handleNewUpload = () => navigate('/');

  const downloadJSON = () => {
    if (!results) return;
    const blob = new Blob([JSON.stringify(results, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'extraction_results.json'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className={`rounded-lg shadow-lg p-8 transition-all duration-300 ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white'
        }`}>
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <button onClick={handleNewUpload} className={`flex items-center space-x-2 transition-colors duration-300 ${darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-800'
              }`}>
              <ArrowLeft className="w-5 h-5" />
              <span>New Upload</span>
            </button>
            <h2 className={`text-3xl font-bold transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'
              }`}>Data Extraction</h2>
          </div>
          {!processing && results && (
            <Button onClick={downloadJSON} className="bg-[#1B5F6E] hover:bg-[#164E5A] text-white">
              <Download className="w-4 h-4 mr-2" /> Download JSON
            </Button>
          )}
        </div>

        <ProcessingStatus steps={steps} currentStep={currentStep} progress={progress} />
      </div>
    </div>
  );
}
